import React from "react";
import {
  Box,
  Container,
  Typography,
  useTheme,
  Fade,
  Grid,
} from "@mui/material";
import { 
  TrendingUp,
  Category,
  Assessment,
} from '@mui/icons-material';
import BrandIcon from '../components/common/BrandIcon';
import { ModernCard, PageContainer } from '../components/common/StyledComponents';

const AboutPage: React.FC = () => {
  const theme = useTheme();

  const features = [
    {
      icon: <TrendingUp />,
      title: "Smart Tracking",
      description: "Track expenses & income with intelligent categorization",
      color: theme.palette.error.main,
    },
    {
      icon: <Category />,
      title: "Custom Categories",
      description: "Organize finances your way with flexible categories",
      color: theme.palette.success.main,
    },
    {
      icon: <Assessment />,
      title: "Visual Reports", 
      description: "Interactive charts and monthly financial insights",
      color: theme.palette.info.main,
    },
  ];

  return (
    <PageContainer>
      <Container maxWidth="lg">
        <Box py={{ xs: 4, md: 8 }}>
          {/* Hero Section */}
          <Fade in timeout={600}>
            <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 10 } }}>
              <Box sx={{ mb: 3 }}>
                <BrandIcon size={80} />
              </Box>
              
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 2,
                  fontSize: { xs: '2rem', md: '3rem' },
                }}
              >
                PocketBalance
              </Typography>
              
              <Typography 
                variant="h5" 
                color="text.secondary" 
                sx={{ 
                  maxWidth: 650, 
                  mx: 'auto',
                  fontWeight: 400,
                  lineHeight: 1.6,
                  fontSize: { xs: '1.1rem', md: '1.5rem' },
                }}
              >
                Track expenses, manage income, and gain financial clarity
              </Typography>
            </Box>
          </Fade>

          {/* Features Grid */}
          <Box sx={{ mb: { xs: 6, md: 8 } }}>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700, 
                mb: 5, 
                textAlign: 'center',
              }}
            >
              Everything You Need
            </Typography>
            <Grid container spacing={3} justifyContent="center">
              {features.map((feature, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <ModernCard
                    sx={{
                      p: 4,
                      height: '100%',
                      border: `1px solid ${theme.palette.divider}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 12px 32px ${feature.color}20`,
                        borderColor: feature.color + '40',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        background: feature.color + '15',
                        mb: 2,
                      }}
                    >
                      {React.cloneElement(feature.icon, {
                        sx: { fontSize: '1.75rem', color: feature.color },
                      })}
                    </Box>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      {feature.description}
                    </Typography>
                  </ModernCard>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Mission Statement */}
          <ModernCard 
            sx={{ 
              p: { xs: 4, md: 6 },
              textAlign: 'center',
              background: `linear-gradient(135deg, ${theme.palette.primary.main}08 0%, ${theme.palette.secondary.main}08 100%)`,
              border: `2px solid ${theme.palette.primary.main}20`,
            }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700, 
                mb: 2,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Built for Financial Clarity
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ 
                maxWidth: 700, 
                mx: 'auto',
                lineHeight: 1.8,
                fontSize: '1.1rem',
              }}
            >
              PocketBalance empowers you to take control of your finances with beautiful, 
              intuitive tools designed to help you make smarter financial decisions.
            </Typography>
          </ModernCard>
        </Box>
      </Container>
    </PageContainer>
  );
};

export default AboutPage;
